import * as React from 'react';
import {
  FieldProps,
  FieldRenderProps as FinalFormFieldRenderProps,
  Field as FinalFormField,
} from 'react-final-form';

type FieldError = string;

// same as lib
export type FieldRenderProps = {
  input: FinalFormFieldRenderProps<HTMLInputElement>['input'];
  meta: {
    /**
     * The relevant field error to display considering formState
     */
    displayError?: FieldError | undefined | null;
  } & FinalFormFieldRenderProps<HTMLInputElement>['meta'];
};

/**
 * Given FieldRenderProps which, if any, error should we display inline?
 */
function fieldRPToError({
  meta: {touched, error, submitError, submitFailed, dirtySinceLastSubmit},
}: FinalFormFieldRenderProps<HTMLInputElement>): FieldError | undefined | null {
  if ((touched || submitFailed) && error) {
    return error;
  }

  if (submitFailed && !dirtySinceLastSubmit && submitError) {
    return submitError;
  }
}

interface PgFieldProps extends FieldProps<HTMLInputElement> {
  children: (rp: FieldRenderProps) => React.ReactNode;
}

/**
 * Our own, slightly enhanced Field component
 * Use in place of react-final-form Field for consistent behavior
 */
export function Field({children, ...otherProps}: PgFieldProps) {
  return (
    <FinalFormField {...otherProps}>
      {fieldRP => {
        const displayError = fieldRPToError(fieldRP);
        return children({
          input: fieldRP.input,
          meta: {
            ...fieldRP.meta,
            displayError,
          },
        });
      }}
    </FinalFormField>
  );
}

Field.defaultProps = {
  /**
   * Prevent form from deleting values on backspace to empty.
   * Related discussion: https://github.com/final-form/react-final-form/issues/342
   *
   * This prevents the following scenario:
   * 1. Backspace field `some_field` to "empty"
   * 2. The `Form.values.some_field` effectively gets deleted
   * 3. Submit form attempting to send `some_field` either as `""` or `null`
   * 4. `some_field` is just not present in the form submission values
   * With this in place, the above scenario would result in `values.some_field * === ''` upon submission.
   */
  parse(value: any) {
    return value || '';
  },
};
